/*
 * Copyright 2026 Sony Corporation
 */

import { SvgIcon, type SvgIconProps } from '@mui/material';

import type { PrimitiveType } from '../../scene-doc/schema';

/**
 * The five primitives, drawn.
 *
 * Hand-drawn rather than picked from the icon set: MUI has no cylinder and no
 * ground plane, and a shape palette where two of five are approximations is
 * worse than no icons at all.
 */

const Shape = ({ children, ...props }: SvgIconProps) => (
  <SvgIcon
    {...props}
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.4}
    strokeLinejoin="round"
    // Not the `fill` attribute: MuiSvgIcon-root sets `fill: currentColor`, and
    // a class beats a presentation attribute, so every shape comes out solid.
    sx={{ fill: 'none' }}
  >
    {children}
  </SvgIcon>
);

const BoxIcon = (props: SvgIconProps) => (
  <Shape {...props}>
    <path d="M12 2.5 21 7v10l-9 4.5L3 17V7z" />
    <path d="M12 12 21 7M12 12 3 7M12 12v9.5" />
  </Shape>
);

const SphereIcon = (props: SvgIconProps) => (
  <Shape {...props}>
    <circle cx="12" cy="12" r="9" />
    <ellipse cx="12" cy="12" rx="9" ry="3.4" />
  </Shape>
);

const CylinderIcon = (props: SvgIconProps) => (
  <Shape {...props}>
    <ellipse cx="12" cy="6" rx="7" ry="3" />
    <path d="M5 6v12M19 6v12" />
    <path d="M5 18a7 3 0 0 0 14 0" />
  </Shape>
);

const PlaneIcon = (props: SvgIconProps) => (
  <Shape {...props}>
    <path d="M3 17 9.5 7h11.5L14.5 17z" />
  </Shape>
);

const GroundIcon = (props: SvgIconProps) => (
  <Shape {...props}>
    <path d="M2 18 8.5 6h13.5L15.5 18z" />
    <path d="M5.5 12h13M10.5 18l4-12" />
  </Shape>
);

export const PRIMITIVE_ICONS: Record<PrimitiveType, (props: SvgIconProps) => React.ReactElement> = {
  box: BoxIcon,
  sphere: SphereIcon,
  cylinder: CylinderIcon,
  plane: PlaneIcon,
  ground: GroundIcon,
};
